import React from 'react'
import {
  Box,
  Divider,
  Flex,
  Grid,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  Calendar,
  Check,
  Info,
  Laptop,
  Mail,
  Paperclip,
  Phone,
  Settings,
} from 'lucide-react'

import { StatusPill } from '@/components/helpdesk/Cell/StatusPill'

export type TicketProgressStep = {
  key: 'submitted' | 'acknowledged' | 'helpdesk_call' | 'resolution'
  label: string
  done?: boolean
}

export type TicketUpdate = {
  id: string
  orgLabel: string
  createdAt: string
  message: string
}

export type TicketInfo = {
  id: string
  statusLabel: 'Open' | 'In Progress' | 'Resolved' | string
  submittedAt: string
  progressMessage?: string

  // Summary
  service?: string
  device?: string
  attachmentsCount?: number
  dispatchAt?: string
  email?: string
  contactName?: string
  contactPhone?: string

  description?: string
  updates?: TicketUpdate[]
  progressSteps?: TicketProgressStep[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  ticket: TicketInfo | null
}

function SummaryRow({
  icon,
  label,
  value,
  fallback = '—',
  showLabel = true,
}: {
  icon: any
  label?: string
  value?: string
  fallback?: string
  showLabel?: boolean
}) {
  const displayValue = value?.trim() ? value : fallback

  return (
    <HStack spacing={3} align="flex-start">
      <Icon as={icon} boxSize={4} color="gray.600" mt="3px" />
      <Text fontSize="sm" color="gray.800" lineHeight="1.7" fontWeight="600">
        {showLabel && label ? (
          <>
            <Text as="span" fontWeight="800">
              {label}:{' '}
            </Text>
            <Text as="span" fontWeight="600">
              {displayValue}
            </Text>
          </>
        ) : (
          <Text as="span" fontWeight="600">
            {displayValue}
          </Text>
        )}
      </Text>
    </HStack>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      p={{ base: 4, md: 5 }}
    >
      <Text color="red.600" fontWeight="800" mb={3}>
        {title}
      </Text>
      {children}
    </Box>
  )
}

function ProgressStepper({
  steps,
  message,
}: {
  steps: TicketProgressStep[]
  message?: string
}) {
  const iconFor = (key: TicketProgressStep['key']) => {
    if (key === 'submitted') return Check
    if (key === 'acknowledged') return Check
    if (key === 'helpdesk_call') return Phone
    return Settings
  }

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 5 }}
    >
      <Flex justify="space-between" align="flex-start" position="relative" pt={1}>
        {steps.map((s, idx) => {
          const next = steps[idx + 1]
          const connectorGreen = s.done && next?.done

          const connectorStyle = connectorGreen
            ? { bg: 'green.400' }
            : {
                bg: 'gray.200',
                backgroundImage:
                  'repeating-linear-gradient(to right, #CBD5E0 0px, #CBD5E0 6px, transparent 6px, transparent 12px)',
                backgroundSize: '12px 2px',
              }

          const StepIcon = iconFor(s.key)

          return (
            <Box key={s.key} flex="1" textAlign="center" position="relative">
              {idx !== steps.length - 1 && (
                <Box position="absolute" top="18px" left="50%" right="-50%" h="2px" {...connectorStyle} />
              )}

              <Box
                mx="auto"
                w="40px"
                h="40px"
                borderRadius="full"
                border="2px solid"
                borderColor={s.done ? 'green.500' : 'gray.300'}
                bg={s.done ? 'green.500' : 'white'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                zIndex={1}
              >
                {s.done ? (
                  <Icon as={Check} boxSize={5} color="white" />
                ) : (
                  <Icon as={StepIcon} boxSize={5} color="gray.300" />
                )}
              </Box>

              <Text mt={2} fontSize="sm" fontWeight="800" color="gray.700">
                {s.label}
              </Text>
            </Box>
          )
        })}
      </Flex>

      {message ? (
        <HStack mt={3} spacing={2} align="center">
          <Icon as={Info} boxSize={4} color="blue.600" />
          <Text fontSize="sm" color="blue.700" fontWeight="700">
            {message}
          </Text>
        </HStack>
      ) : null}
    </Box>
  )
}

export function RequestInfoModal({ isOpen, onClose, ticket }: Props) {
  const steps =
    ticket?.progressSteps ??
    ([
      { key: 'submitted', label: 'Submitted', done: true },
      { key: 'acknowledged', label: 'Acknowledged', done: false },
      { key: 'helpdesk_call', label: 'Helpdesk Call', done: false },
      { key: 'resolution', label: 'Resolution', done: false },
    ] as TicketProgressStep[])

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.500" />

      <ModalContent
        borderRadius="xl"
        overflow="hidden"
        bg="gray.50"
        w="full"
        maxW={{ base: '94vw', md: '680px' }} // ✅ not wide
        mx={{ base: 3, md: 0 }}
      >
        <ModalHeader pb={3} bg="white" borderBottom="1px solid" borderColor="gray.200">
          <Text color="red.600" fontWeight="800" fontSize="md">
            Ticket Information
          </Text>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody pb={6} pt={5} px={{ base: 4, md: 6 }}>
          {!ticket ? (
            <Box py={10}>
              <Text color="gray.500">No ticket selected.</Text>
            </Box>
          ) : (
            <Stack
              spacing={4}
              maxH={{ base: '70vh', md: '72vh' }}
              overflowY="auto"
              pr={{ base: 0, md: 2 }}
            >
              {/* Header row */}
              <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={{ base: 4, md: 5 }}>
                <HStack justify="space-between" align="flex-start" spacing={4} flexWrap="wrap">
                  <Box>
                    <Text fontWeight="800" color="gray.800" fontSize="md">
                      Ticket: <Text as="span"># {ticket.id}</Text>
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={1}>
                      Track the status and progress of your request
                    </Text>
                  </Box>

                  <HStack spacing={4} align="center">
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">
                        Submitted
                      </Text>
                      <Icon as={Calendar} boxSize={4} color="gray.500" />
                      <Text fontSize="sm" fontWeight="700" color="gray.700">
                        {ticket.submittedAt}
                      </Text>
                    </HStack>

                    <StatusPill label={ticket.statusLabel} />
                  </HStack>
                </HStack>
              </Box>

              {/* Progress */}
              <Section title="Ticket Progress">
                <ProgressStepper steps={steps} message={ticket.progressMessage} />
              </Section>

              {/* Summary */}
              <Section title="Summary">
                <Grid
                  templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                  gap={{ base: 4, md: 6 }}
                  position="relative"
                  alignItems="start"
                >
                  <Box
                    display={{ base: 'none', md: 'block' }}
                    position="absolute"
                    left="50%"
                    top={1}
                    bottom={1}
                    w="1px"
                    bg="gray.200"
                    transform="translateX(-0.5px)"
                  />

                  <VStack align="start" spacing={4} pr={{ base: 0, md: 3 }}>
                    <SummaryRow icon={Settings} label="Service" value={ticket.service} />
                    <SummaryRow icon={Laptop} label="Device" value={ticket.device} />
                    <SummaryRow
                      icon={Paperclip}
                      label="Attachments"
                      value={
                        typeof ticket.attachmentsCount === 'number'
                          ? `${ticket.attachmentsCount} image${ticket.attachmentsCount === 1 ? '' : 's'}`
                          : undefined
                      }
                    />
                  </VStack>

                  <VStack align="start" spacing={4} pl={{ base: 0, md: 3 }}>
                    <SummaryRow icon={Calendar} label="Dispatch"/>
                    <SummaryRow icon={Mail} value={ticket.email} showLabel={false} />
                    <SummaryRow
                      icon={Phone}
                      value={
                        ticket.contactName && ticket.contactPhone
                          ? `${ticket.contactName} • ${ticket.contactPhone}`
                          : (ticket.contactName ?? ticket.contactPhone)
                      }
                      showLabel={false}
                    />
                  </VStack>
                </Grid>
              </Section>

              {/* Description */}
              <Section title="Description">
                <Text fontSize="sm" color="gray.800" lineHeight="1.9" whiteSpace="pre-wrap">
                  {ticket.description || '—'}
                </Text>
              </Section>

              {/* Updates */}
              <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={{ base: 4, md: 5 }}>
                <HStack spacing={2} mb={3}>
                  <Box w="8px" h="8px" borderRadius="full" bg="green.500" />
                  <Text fontWeight="800" color="red.600">
                    Updates
                  </Text>
                </HStack>

                <Divider />

                <Box pt={3}>
                  {ticket.updates?.length ? (
                    <Stack spacing={4}>
                      {ticket.updates.map((u) => (
                        <Box key={u.id}>
                          <Text fontSize="sm" fontWeight="800" color="gray.800">
                            {u.orgLabel}{' '}
                            <Text as="span" color="gray.400" fontWeight="700">
                              • {u.createdAt}
                            </Text>
                          </Text>

                          <Text fontSize="sm" color="gray.800" mt={2} lineHeight="1.8">
                            {u.message}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No updates yet.
                    </Text>
                  )}
                </Box>
              </Box>
            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
